import { Link } from '@/global/components/Link'
import { useTransmit } from '@/hooks/useTransmit'
import { cn } from '@/lib/utils'
import {
    notificacoes_api,
    NotificationMessage,
} from '@/service/api/endpoints/notificacoes'
import { useAuth } from 'hooks/useAuth'
import { BellIcon, ExternalLinkIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import IconButton from '../../global/components/Button/IconButton'
import { Badge } from '../../global/components/ui/badge'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../global/components/ui/dropdown-menu'
import NenhumaNotificacao from './NenhumaNotificacao'
import Notificacao from './Notificacao'
import { useNotificacoesSilentStore } from './store'

const fetchedMissedByUser = new Set<number>()

export function CentralNotificacoes() {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { decoded } = useAuth()
    const [notifications, setNotifications] = useState<
        (NotificationMessage & { seen: boolean })[]
    >([])
    const [open, setOpen] = useState(false)
    const updateSilent = useNotificacoesSilentStore(state => state.update)
    const subscribe = useTransmit<NotificationMessage>({
        onMessage: payload => {
            if (payload.mode === 'silent') {
                updateSilent(payload)
                return
            }

            for (const mode of payload.mode) {
                switch (mode) {
                    case 'toast':
                        toast[payload.severity || 'info'](payload.message, {
                            id: payload.id,
                            description: payload.title,
                            actionButtonStyle: {
                                background: 'transparent',
                                color: 'inherit',
                                padding: 0,
                            },
                            action: payload.onClickUrl
                                ? {
                                      label: (
                                          <div
                                              title={payload.onClickUrl}
                                              className="flex items-center gap-1"
                                          >
                                              <ExternalLinkIcon className="size-4" />
                                          </div>
                                      ),
                                      type: 'div',
                                      onClick: () => {
                                          payload.onClickUrl!.startsWith('http')
                                              ? window.open(
                                                    payload.onClickUrl,
                                                    '_blank',
                                                )
                                              : navigate(payload.onClickUrl!)
                                      },
                                  }
                                : undefined,
                        })
                        break
                    case 'modal':
                        alert(`${payload.title}\n\n${payload.message}`) // temp
                        break
                    case 'badge':
                        addNotification(payload)
                        break
                    default:
                        throw new Error(
                            `Invalid notification mode: ${
                                mode satisfies never
                            }`,
                        )
                }
            }
        },
    })

    function addNotification(message: NotificationMessage) {
        return setNotifications(prev =>
            prev.some(n => n.id === message.id)
                ? prev
                : [...prev, { ...message, seen: false }].sort(
                      (a, b) =>
                          new Date(b.createdAt || '').getTime() -
                          new Date(a.createdAt || '').getTime(),
                  ),
        )
    }

    function markAsSeen(id: string | '*') {
        notificacoes_api.post('offset', {} as any)
        setNotifications(prev => {
            if (id === '*') {
                return prev.map(n => ({ ...n, seen: true }))
            }

            return prev.map(n => (n.id === id ? { ...n, seen: true } : n))
        })
    }

    useEffect(() => {
        if (!decoded.sub || decoded.sub < 1) return

        const channel = `notificacao/user/${decoded.sub}`
        let isCancelled = false
        let unsubscribeFn: (() => void) | undefined
        ;(async () => {
            const unsubscribe = await subscribe(channel)

            if (isCancelled) {
                await unsubscribe()
                return
            }

            unsubscribeFn = unsubscribe

            if (!fetchedMissedByUser.has(decoded.sub)) {
                fetchedMissedByUser.add(decoded.sub)
                notificacoes_api.get('missed')
            }
        })()

        return () => {
            isCancelled = true
            unsubscribeFn?.()
        }
    }, [decoded.sub])

    useEffect(() => {
        setOpen(false)
    }, [pathname])

    const notificacoesNaoLidas = Math.min(
        notifications.filter(n => !n.seen).length,
        99,
    )

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger>
                <IconButton
                    component={'div'}
                    className="dark:text-foreground text-accent"
                >
                    <Badge
                        className={cn(
                            'absolute -top-1 -right-1 size-4 rounded-full',
                            'flex items-center justify-center',
                            'p-0 dark:bg-primary bg-accent/80',
                            'dark:text-foreground text-accent-foreground',
                        )}
                    >
                        {notificacoesNaoLidas > 0 ? notificacoesNaoLidas : null}
                    </Badge>
                    <BellIcon className="size-5" />
                </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                <div className="flex flex-col w-xs max-w-[calc(100vw-2rem)] h-80">
                    <div className="w-full flex items-center justify-between px-1 py-2">
                        <span className="select-none px-2">Notificações</span>
                        {notificacoesNaoLidas > 0 && (
                            <Link
                                className="text-xs cursor-pointer"
                                onClick={() => markAsSeen('*')}
                            >
                                Marcar todas como lidas
                            </Link>
                        )}
                    </div>
                    <DropdownMenuSeparator />
                    {notifications.length === 0 ? (
                        <NenhumaNotificacao />
                    ) : (
                        <div className="flex flex-col max-h-full overflow-y-auto py-2 gap-2 px-1">
                            {notifications.map(notification => (
                                <Notificacao
                                    key={notification.id}
                                    data={notification}
                                    onMouseEnter={() =>
                                        markAsSeen(notification.id)
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
